import { useEffect, useState, type ReactNode } from 'react';
import { App, Button, Checkbox, Collapse, DatePicker, Flex, Input, Select, Space, Tag, Typography } from 'antd';
import { EllipsisOutlined, PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { formatQty, formatStock, MAX_LINE_QTY, MAX_PCS_PER_BOX, qty } from '@app/shared';
import { FloatingWindow } from '@/shared/ui/FloatingWindow';
import { LinesGrid, useLinesGridApi } from '@/shared/ui/LinesGrid';
import type { LinesGridColumn } from '@/shared/ui/LinesGrid';
import { HotkeyButton, HotkeyTip } from '@/shared/ui/hotkey';
import {
  INVALID_INPUTS_HINT,
  INVALID_INPUTS_REASON,
  IntInput,
  InvalidInputsContext,
  MoneyInput,
  QtyInput,
  commitActiveField,
  useFocusTarget,
  useInvalidInputs,
} from '@/shared/ui/inputs';
import { useHotkeyActions } from '@/shared/lib/hotkeys';
import type { BoundAction, HotkeyActionId } from '@/shared/lib/hotkeys';
import { useBreakpoint, useLatest } from '@/shared/lib/hooks';
import { CategorySearchSelect } from '@/entities/category';
import type { CategoryDto } from '@/entities/category';
import { CounterpartySelect } from '@/entities/counterparty';
import {
  useCancelDocumentMutation,
  useCreateDocumentMutation,
  useDeleteDocumentMutation,
  useDocumentCashOrdersQuery,
  useDuplicateTemplateQuery,
  useGetDocumentQuery,
  usePostDocumentMutation,
  usePreviewDocumentMutation,
  useRestoreDocumentMutation,
  useReturnDocumentToDraftMutation,
  useReturnTemplateQuery,
  useUnpostDocumentMutation,
  useUpdateDocumentMutation,
} from '@/entities/document';
import type { DocumentCounterpartyRef, DocumentDto, LineWarningEntry, WriteOffReason } from '@/entities/document';
import { BatchSelect } from '@/entities/stock';
import { AttachmentsBlock } from '@/features/attachments';
import { PreviewConsequencesDialog, useSaveWithPreview } from '@/features/document/preview-consequences';
import { isVersionConflict } from '@/features/document/concurrent-edit';
import { BottomSheet, UnsavedChangesDialog } from '@/shared/ui';
import { useWindowHost, useWindowManager } from '@/shared/lib/windows';
import { useStoreContext } from '@/shared/lib/store-context';
import { ownerPath } from '@/shared/config/routes';
import { formatDate, todayInTz } from '@/shared/lib/date';
import { describeWarnings } from '@/shared/lib/warnings';
import { getErrorCode, getErrorDetails, translateApiError } from '@/shared/api/errorToMessage';
import type { ApiQueryError } from '@/shared/api/errorToMessage';
import type { EditorDocumentType } from './model';
import {
  amountOf,
  applyCategoryDefaults,
  archivedLinesMessage,
  cashOrderTargetType,
  emptyLine,
  fillFromStock,
  adoptLineIds,
  footerTotals,
  fromDocumentLine,
  fromDuplicateTemplate,
  fromReturnTemplate,
  insertCreatedCategory,
  inventoryDiff,
  getPostBlockReason,
  postedQtyByCategory,
  qtyOf,
  recalcMarkup,
  reuseKeys,
  shouldReseed,
  toLineInput,
  windowTitle,
  windowWarnings,
} from './model';
import type { EditorLine } from './model';

const PAYMENT_STATUS_LABEL: Record<string, string> = { UNPAID: 'не оплачено', PARTIAL: 'частично', PAID: 'оплачено' };
/** §7/§11.2: у возврата «оплачено» — то, чем он закрыт: зачтено в долги + возвращено деньгами. */
const RETURN_SETTLEMENT_LABEL: Record<string, string> = { UNPAID: 'не закрыт', PARTIAL: 'закрыт частично', PAID: 'закрыт' };
const CASH_ORDER_PREFIX: Record<'CASH_IN' | 'CASH_OUT', string> = { CASH_IN: 'ПКО', CASH_OUT: 'РКО' };
const CASH_ORDER_STATUS_LABEL: Record<string, string> = { DRAFT: 'черновик', POSTED: 'проведён', CANCELLED: 'отменён' };
const WRITE_OFF_REASON_OPTIONS: { value: WriteOffReason; label: string }[] = [
  { value: 'SPOILAGE', label: 'Порча' },
  { value: 'EXPIRED', label: 'Истёк срок' },
  { value: 'SHORTAGE', label: 'Недостача' },
  { value: 'DEFECT', label: 'Брак' },
  { value: 'OTHER', label: 'Другое' },
];

export interface DocumentWindowProps {
  type: EditorDocumentType;
  id?: string;
  /** §11.2/§11.3 «Создать возврат»: id проведённой продажи/прихода-основания для нового документа. */
  returnTemplateFrom?: string;
  /** §12.6 «Дублировать»: id документа-источника для нового документа. */
  copyOf?: string;
  /** Номер документа-источника для заголовка «Новый приход (копия 125/2026)». */
  copyOfLabel?: string;
  onClose: () => void;
}

export function DocumentWindow({ type, id, returnTemplateFrom, copyOf, copyOfLabel, onClose }: DocumentWindowProps) {
  const { message, modal } = App.useApp();
  const ctx = useStoreContext();
  const [documentId, setDocumentId] = useState(id);
  const { gridApiRef, api: gridApi } = useLinesGridApi();
  // Куда уходит курсор по `Ctrl+↑` («к шапке»): контрагент, а у списания и инвентаризации — комментарий.
  const { attach: attachCounterparty, focus: focusCounterparty } = useFocusTarget<HTMLDivElement>();
  const { attach: attachComment, focus: focusComment } = useFocusTarget<HTMLDivElement>();

  const { data: doc, isFetching, refetch: refetchDoc } = useGetDocumentQuery(documentId ?? '', { skip: !documentId });
  const [createDocument, { isLoading: isCreating }] = useCreateDocumentMutation();
  const [updateDocument, { isLoading: isUpdating }] = useUpdateDocumentMutation();
  const [postDocument, { isLoading: isPosting }] = usePostDocumentMutation();
  const [unpostDocument, { isLoading: isUnposting }] = useUnpostDocumentMutation();
  const [cancelDocument, { isLoading: isCancelling }] = useCancelDocumentMutation();
  const [deleteDocument, { isLoading: isDeleting }] = useDeleteDocumentMutation();
  const [returnToDraft, { isLoading: isReturningToDraft }] = useReturnDocumentToDraftMutation();
  const [restoreDocument, { isLoading: isRestoring }] = useRestoreDocumentMutation();
  const [previewDocument] = usePreviewDocumentMutation();
  // «Провести»: от нажатия до ответа сервера, включая проверку предупреждений и вопрос владельцу.
  const [isPosting_, setIsPosting_] = useState(false);
  const { runWithPreview, pendingDiff, resolvePending, isRunningPreview } = useSaveWithPreview();
  const windows = useWindowManager();
  // Телефон (< 768px): шапка вертикальной формой, строки карточками, действия — в нижней панели.
  const { isMobile } = useBreakpoint();
  // Меню «⋯» нижней панели телефона: все действия документа, кроме главного.
  const [menuOpen, setMenuOpen] = useState(false);

  const [header, setHeader] = useState<{
    docDate: string;
    counterpartyId?: string;
    comment: string;
    baseDocumentId?: string;
  }>({
    docDate: todayInTz(ctx.timezone),
    comment: '',
  });
  const [lines, setLines] = useState<EditorLine[]>([emptyLine(1)]);
  // Название контрагента документа — для поля выбора: архивного контрагента в списке нет.
  const [counterpartyRef, setCounterpartyRef] = useState<DocumentCounterpartyRef | undefined>(undefined);
  const [dirty, setDirty] = useState(false);
  // §: индикатор несохранённых изменений на плашке в панели свёрнутых окон — без разворачивания
  // окна видно, в каком черновике остались непроведённые правки.
  const windowHost = useWindowHost();
  useEffect(() => {
    windowHost?.setDirty(dirty);
  }, [windowHost, dirty]);

  function seedFrom(d: DocumentDto) {
    setSeededDoc(d);
    setHeader({
      docDate: d.docDate.slice(0, 10),
      counterpartyId: d.counterpartyId ?? undefined,
      comment: d.comment ?? '',
      baseDocumentId: d.baseDocumentId ?? undefined,
    });
    setCounterpartyRef(d.counterparty ?? undefined);
    const withStock = !(d.type === 'INVENTORY' && d.status !== 'DRAFT');
    const readOnly = d.status === 'CANCELLED' || !!d.deletedAt;
    const seeded =
      d.lines.length > 0
        ? [...d.lines.map((l) => fromDocumentLine(l, withStock)), ...(readOnly ? [] : [emptyLine(d.lines.length + 1)])]
        : [emptyLine(1)];
    setLines((prev) => reuseKeys(prev, seeded));
    setDirty(false);
  }

  // Loaded doc → seed the editor (render-phase, not an effect — see React docs on adjusting state
  // when a prop changes). Пересев повторяется при каждом обновлении документа с сервера: после
  // проведения в строках появляется себестоимость, после чужих правок — новые суммы. Несохранённые
  // правки владельца при этом не затираются (см. `shouldReseed`).
  const [seededDoc, setSeededDoc] = useState<DocumentDto | undefined>(undefined);
  if (doc && doc !== seededDoc && shouldReseed(seededDoc, doc, dirty)) {
    seedFrom(doc);
  }

  /** §8.7 «Параллельная правка»: VERSION_CONFLICT предлагает открыть свежую версию (отбрасывая
   * локальные правки) вместо тупикового тоста; любая другая ошибка идёт через обычный toast. */
  function handleMutationError(e: unknown) {
    if (!isVersionConflict(e)) {
      message.error(describeError(e));
      return;
    }
    modal.confirm({
      title: 'Документ изменён в другом окне',
      content: 'Кто-то успел сохранить более новую версию. Открыть свежую версию? Ваши несохранённые изменения будут потеряны.',
      okText: 'Открыть свежую версию',
      cancelText: 'Отмена',
      autoFocusButton: 'cancel',
      onOk: async () => {
        const result = await refetchDoc();
        if (result.data) seedFrom(result.data);
      },
    });
  }

  // §11.2/§11.3 «Создать возврат»: новый документ (без id) открытый с основанием — подтягиваем
  // шаблон один раз, тем же render-phase-seed паттерном, что и для загруженного doc выше.
  const { data: returnTemplate } = useReturnTemplateQuery(returnTemplateFrom ?? '', { skip: !returnTemplateFrom || !!id });
  const [seededTemplateFrom, setSeededTemplateFrom] = useState<string | undefined>(undefined);
  if (returnTemplate && returnTemplateFrom && returnTemplateFrom !== seededTemplateFrom) {
    setSeededTemplateFrom(returnTemplateFrom);
    setHeader((h) => ({ ...h, counterpartyId: returnTemplate.counterpartyId ?? undefined, baseDocumentId: returnTemplate.baseDocumentId }));
    setCounterpartyRef(returnTemplate.counterparty ?? undefined);
    setLines(fromReturnTemplate(returnTemplate));
  }

  // §12.6 «Дублировать»: новый документ (без id) открытый с источником — тот же паттерн.
  const { data: duplicateTemplate } = useDuplicateTemplateQuery(copyOf ?? '', { skip: !copyOf || !!id });
  const [seededCopyOf, setSeededCopyOf] = useState<string | undefined>(undefined);
  if (duplicateTemplate && copyOf && copyOf !== seededCopyOf) {
    setSeededCopyOf(copyOf);
    setHeader((h) => ({ ...h, counterpartyId: duplicateTemplate.counterpartyId ?? undefined, comment: duplicateTemplate.comment ?? '' }));
    setCounterpartyRef(duplicateTemplate.counterparty ?? undefined);
    setLines(fromDuplicateTemplate(duplicateTemplate));
  }

  const status = doc?.status ?? 'DRAFT';
  const isNew = !documentId;
  // §8.1/§8.7: удалённый документ хранит прежний статус, но окно у него своё — только чтение и
  // «Восстановить»/«Дублировать»/«Закрыть».
  const isDeleted = !!doc?.deletedAt;
  // §8.3/§8.7: проведённый документ теперь тоже редактируется («Сохранить» — перепроведение с
  // предпросмотром последствий по §8.5); отменённый и удалённый остаются полностью read-only
  // (см. renderFooter).
  const isReadOnly = status === 'CANCELLED' || isDeleted;
  const busy =
    isCreating ||
    isUpdating ||
    isPosting ||
    isPosting_ ||
    isUnposting ||
    isCancelling ||
    isDeleting ||
    isReturningToDraft ||
    isRestoring ||
    isRunningPreview;
  const { data: cashOrders } = useDocumentCashOrdersQuery(documentId ?? '', {
    skip: !documentId || isDeleted || cashOrderTargetType(type) === null,
  });

  /** Текст отказа: для архивного товара — с номером строки и названием, для занятой заявки — с
   * подсказкой, что делать; остальное — общий словарь по коду ошибки. */
  function describeError(e: unknown): string {
    const code = getErrorCode(e as ApiQueryError);
    if (code === 'CATEGORY_ARCHIVED') return archivedLinesMessage(getErrorDetails(e), lines) ?? translateApiError(e);
    if (code === 'ORDER_REQUEST_ALREADY_CONVERTED') {
      return `${translateApiError(e)}. Нужен такой же товар в новой продаже — используйте «Дублировать»`;
    }
    return translateApiError(e);
  }

  function updateLine(key: string, patch: Partial<EditorLine>) {
    setDirty(true);
    setLines((prev) => prev.map((l) => (l.key === key ? recalcMarkup({ ...l, ...patch }) : l)));
  }

  /** Новая строка (Enter в последнем поле, Insert). Пустую строку в конце не плодим: она и так ждёт
   *  ввода — просто встаём на неё; `false` говорит таблице, что переходить на «новую» строку не
   *  нужно. */
  function addRow(): boolean {
    const last = lines[lines.length - 1];
    if (last && !last.categoryId) {
      gridApi.focusCell(lines.length - 1, 0);
      return false;
    }
    setDirty(true);
    setLines((prev) => [...prev, emptyLine(prev.length + 1)]);
    return true;
  }

  /** Выбор товара в строке — как `updateLine`, но если это была последняя (ранее пустая) строка,
   *  сразу добавляет новую пустую строку снизу, чтобы можно было продолжить без Enter/кнопки.
   *  §5: после выбора курсор встаёт в первое числовое поле этой же строки («Коробок»). */
  function selectCategory(line: EditorLine, rowIndex: number, category: CategoryDto) {
    setDirty(true);
    setLines((prev) => {
      const next = prev.map((l) => (l.key === line.key ? recalcMarkup(applyCategoryDefaults(l, category, type)) : l));
      if (rowIndex === prev.length - 1 && !line.categoryId) {
        return [...next, emptyLine(next.length + 1)];
      }
      return next;
    });
    gridApi.focusCell(rowIndex, 1);
  }

  /** Созданный в окне категории товар сразу встаёт в таблицу — в строку, из которой нажали «+» (если
   *  она пустая), иначе в первую пустую — и курсор уходит в её первое числовое поле. Фокус — в
   *  следующем кадре: окно категории закрывается в том же событии и при закрытии возвращает фокус
   *  тому, кто его открыл, а строка могла только что появиться. */
  function insertCreated(preferredKey: string | undefined, category: CategoryDto) {
    const { lines: next, rowIndex } = insertCreatedCategory(lines, preferredKey, category, type);
    setDirty(true);
    setLines(next);
    requestAnimationFrame(() => gridApi.focusCell(rowIndex, 1));
  }

  /** «+» у строки и Alt+Shift+K: окно новой категории. Оно отвечает позже, чем «+» создал замыкание
   *  (за это время строки могли измениться), поэтому вставку берём из `latest` — свежую. */
  function openNewCategory(preferredKey?: string) {
    windows.open({ kind: 'category', onCreated: (category) => latest.current.insertCreated(preferredKey, category) });
  }

  function removeRow(key: string) {
    setDirty(true);
    setLines((prev) => (prev.length <= 1 ? prev : prev.filter((l) => l.key !== key).map((l, i) => ({ ...l, lineNo: i + 1 }))));
  }

  function handleFillFromStock() {
    setDirty(true);
    setLines((prev) => fillFromStock(prev));
  }

  function buildPayload() {
    return {
      docDate: header.docDate,
      counterpartyId: header.counterpartyId,
      comment: header.comment || undefined,
      lines: lines.filter((l) => l.categoryId).map((l) => toLineInput(l, type)),
    };
  }

  async function ensureCreated(): Promise<string> {
    if (documentId) return documentId;
    const created = await createDocument({ type, baseDocumentId: header.baseDocumentId, copiedFromDocumentId: copyOf, ...buildPayload() }).unwrap();
    setLines((prev) => adoptLineIds(prev, created.lines));
    setDocumentId(created.id);
    setDirty(false);
    return created.id;
  }

  /** Кнопки при неверном поле недоступны; сюда можно прийти ещё из диалога «Сохранить изменения?»
   *  и из горячей клавиши — для них проверка повторяется здесь, а не только в `disabled` кнопки. */
  function refuseInvalidInput(): boolean {
    if (canSave) return false;
    message.warning(INVALID_INPUTS_HINT);
    return true;
  }

  /** То же для «Провести»/«Сохранить» проведённого: документ должен быть полон. Причину называем
   *  словами — по клавише пользователь не видит подсказку на отключённой кнопке. */
  function refuseIncompleteDocument(): boolean {
    if (refuseInvalidInput()) return true;
    if (postBlockReason === null) return false;
    message.warning(postBlockReason);
    return true;
  }

  async function handleSaveDraft(close: boolean) {
    if (busy || refuseInvalidInput()) return;
    try {
      if (!documentId) {
        await ensureCreated();
      } else {
        const updated = await updateDocument({ id: documentId, rowVersion: doc!.rowVersion, body: buildPayload() }).unwrap();
        setLines((prev) => adoptLineIds(prev, updated.lines));
        setDirty(false);
      }
      message.success('Черновик сохранён');
      if (close) onClose();
    } catch (e) {
      handleMutationError(e);
    }
  }

  /** Предупреждения словами, с названиями товаров из строк окна. Архивный товар сюда не входит:
   * это не предупреждение, а запрет, и у него своё сообщение. */
  function warningTexts(warnings: LineWarningEntry[]): string[] {
    return describeWarnings(
      warnings.filter((w) => w.code !== 'ARCHIVED_CATEGORY'),
      (lineNo) => lines.find((l) => l.lineNo === lineNo && l.categoryId)?.categoryName,
    );
  }

  /** §1, §5: система не запрещает, а предупреждает — и спрашивает до проведения, пока минус ещё
   * не записан. Отказ оставляет документ черновиком. */
  function confirmWarnings(texts: string[]): Promise<boolean> {
    return new Promise((resolve) => {
      modal.confirm({
        title: 'Провести с предупреждениями?',
        content: (
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {texts.map((text) => (
              <li key={text}>{text}</li>
            ))}
          </ul>
        ),
        okText: 'Провести',
        cancelText: 'Не проводить',
        width: 560,
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });
  }

  async function handlePost(close: boolean) {
    if (busy || refuseIncompleteDocument()) return;
    setIsPosting_(true);
    try {
      const currentId = await ensureCreated();
      const rowVersion = documentId ? (doc?.rowVersion ?? 1) : 1;
      const payload = documentId ? buildPayload() : undefined;
      // Предпросмотр проводит документ «начерно» и откатывает: предупреждения известны заранее.
      const preview = await previewDocument({ id: currentId, body: { action: 'post', rowVersion, document: payload } }).unwrap();
      const texts = warningTexts(preview.warnings);
      if (texts.length > 0 && !(await confirmWarnings(texts))) {
        message.info('Документ не проведён и остаётся черновиком');
        return;
      }
      const result = await postDocument({ id: currentId, body: { rowVersion, document: payload } }).unwrap();
      // Себестоимость и сумму потери считает склад при проведении — показываем их сразу, из ответа.
      seedFrom(result.document);
      message.success(result.warnings.length > 0 ? 'Документ проведён с предупреждениями' : 'Документ проведён');
      if (close) onClose();
    } catch (e) {
      handleMutationError(e);
    } finally {
      setIsPosting_(false);
    }
  }

  /** §8.3/§8.5/§8.7: «Сохранить» проведённого документа — перепроведение с правкой строк/шапки,
   * с предпросмотром последствий перед сохранением. */
  async function handleSaveRepost(close: boolean) {
    if (busy || !documentId || !doc || refuseIncompleteDocument()) return;
    const payload = buildPayload();
    let warnings: LineWarningEntry[] = [];
    try {
      const saved = await runWithPreview(
        documentId,
        'post',
        doc.rowVersion,
        async (stateVersion) => {
          const result = await postDocument({ id: documentId, body: { rowVersion: doc.rowVersion, document: payload, stateVersion } }).unwrap();
          warnings = result.warnings;
          // Сидируем локальный `lines`/`header` напрямую из ответа мутации — она гарантированно
          // свежая (перепроведённая формой на сервере), а кэш `useGetDocumentQuery` обновится
          // позже; до тех пор его устаревшую версию отсекает `shouldReseed`.
          seedFrom(result.document);
        },
        payload,
      );
      if (saved) {
        message.success(warnings.length > 0 ? 'Документ перепроведён с предупреждениями' : 'Документ перепроведён');
        if (close) onClose();
      }
    } catch (e) {
      handleMutationError(e);
    }
  }

  /** §8.5: снятие проведения/отмена/удаление проведённого документа — предпросмотр последствий
   * перед сохранением (не блокирует, только предупреждает). */
  /** Деструктивные действия (снять проведение, отменить, удалить) всегда переспрашивают, а фокус
   *  в диалоге стоит на «Отмена»: случайный Enter ничего не разрушает. */
  function handleUnpost() {
    if (busy || !documentId || !doc) return;
    modal.confirm({
      title: 'Снять проведение?',
      content: 'Остатки и расчёты по документу будут пересчитаны, а документ вернётся в черновик.',
      okText: 'Снять проведение',
      cancelText: 'Отмена',
      okButtonProps: { danger: true },
      autoFocusButton: 'cancel',
      onOk: async () => {
        try {
          const saved = await runWithPreview(documentId, 'unpost', doc.rowVersion, (stateVersion) =>
            unpostDocument({ id: documentId, body: { rowVersion: doc.rowVersion, stateVersion } }).unwrap(),
          );
          if (saved) message.success('Проведение снято');
        } catch (e) {
          handleMutationError(e);
        }
      },
    });
  }

  function handleCancel() {
    if (busy || !documentId || !doc) return;
    modal.confirm({
      title: 'Отменить документ?',
      okText: 'Отменить документ',
      cancelText: 'Не отменять',
      okButtonProps: { danger: true },
      autoFocusButton: 'cancel',
      onOk: async () => {
        try {
          const saved = await runWithPreview(documentId, 'cancel', doc.rowVersion, (stateVersion) =>
            cancelDocument({ id: documentId, body: { rowVersion: doc.rowVersion, stateVersion } }).unwrap(),
          );
          if (saved) message.success('Документ отменён');
        } catch (e) {
          handleMutationError(e);
        }
      },
    });
  }

  function handleDelete() {
    if (busy || !documentId || !doc) return;
    modal.confirm({
      title: 'Удалить документ?',
      okText: 'Удалить',
      cancelText: 'Отмена',
      okButtonProps: { danger: true },
      autoFocusButton: 'cancel',
      onOk: async () => {
        try {
          const saved =
            status === 'POSTED'
              ? await runWithPreview(documentId, 'remove', doc.rowVersion, (stateVersion) =>
                  deleteDocument({ id: documentId, body: { rowVersion: doc.rowVersion, stateVersion } }).unwrap(),
                )
              : await deleteDocument({ id: documentId, body: { rowVersion: doc.rowVersion } })
                  .unwrap()
                  .then(() => true);
          if (saved) {
            message.success('Документ удалён');
            onClose();
          }
        } catch (e) {
          handleMutationError(e);
        }
      },
    });
  }

  async function handleReturnToDraft() {
    if (busy || !documentId || !doc) return;
    try {
      const result = await returnToDraft({ id: documentId, rowVersion: doc.rowVersion }).unwrap();
      seedFrom(result.document);
      message.success('Документ возвращён в черновик');
    } catch (e) {
      handleMutationError(e);
    }
  }

  /** §8.1: удалённый проведённый возвращается черновиком — влияние на склад и долги появится
   * только после повторного проведения. */
  async function handleRestore() {
    if (busy || !documentId || !doc) return;
    try {
      const result = await restoreDocument({ id: documentId, rowVersion: doc.rowVersion }).unwrap();
      seedFrom(result.document);
      message.success(result.document.status === 'DRAFT' ? 'Документ восстановлен как черновик — проведите его заново' : 'Документ восстановлен');
    } catch (e) {
      handleMutationError(e);
    }
  }

  const footer = footerTotals(type, lines, status === 'POSTED' && !isDeleted);
  const hasArchivedLine = lines.some((l) => l.categoryId && l.archived);
  // Предупреждения видны всё время, пока документ открыт: те, что пришли с сервера, и подсказка
  // о минусе по уже введённым количествам (до сохранения и проведения).
  const warningsView = isReadOnly ? { texts: [], lineNos: new Set<number>() } : windowWarnings(type, lines, seededDoc?.warnings ?? [], postedQtyByCategory(seededDoc));
  // Поле с неверным значением отдаёт наружу «пусто»: у необязательного поля («Оплачено», оптовая
  // цена) это выглядело бы как «не заполнено», и документ сохранился бы без введённого.
  const invalidInputs = useInvalidInputs();
  // Причина недоступности («Укажите контрагента») нужна и подсказке на отключённой кнопке, и
  // сообщению, если действие вызвали горячей клавишей; `null` — действие доступно.
  const saveBlockReason = invalidInputs.hasInvalid ? INVALID_INPUTS_REASON : null;
  const postBlockReason = saveBlockReason ?? getPostBlockReason(type, header, lines);
  const canSave = saveBlockReason === null;

  // §1.8/§8.7: предупреждение о несохранённых изменениях не только при закрытии окна внутри
  // приложения, но и при закрытии вкладки/перезагрузке браузера целиком.
  useEffect(() => {
    if (!dirty) return;
    function handler(e: BeforeUnloadEvent) {
      e.preventDefault();
      e.returnValue = '';
    }
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);

  /** §8.7: «Закрытие с несохранёнными изменениями» — Сохранить(-черновик) / Не сохранять / Отмена. */
  function handleClose() {
    if (!dirty) {
      onClose();
      return;
    }
    setShowUnsavedDialog(true);
  }

  async function handleSaveAndClose() {
    setShowUnsavedDialog(false);
    if (status === 'DRAFT') {
      await handleSaveDraft(true);
    } else {
      await handleSaveRepost(true);
    }
  }

  // ── Горячие клавиши окна ────────────────────────────────────────────────────────────────
  // Клавиша приходит раньше, чем поле ввода отдало окну набранное (коробки, цена и дата фиксируются
  // на blur), а замыкания биндингов — от прошлой перерисовки. Поэтому действие сначала фиксирует
  // активное поле (окно при этом перерисовывается), а затем берёт обработчик из `latest` —
  // свежего после каждой перерисовки; каждый обработчик ещё раз проверяет `busy` и полноту.
  const latest = useLatest({
    handlePost,
    handleSaveDraft,
    handleSaveRepost,
    handleUnpost,
    handleCancel,
    handleDelete,
    handleReturnToDraft,
    handleRestore,
    handleDuplicate,
    handlePrint,
    handleCreateReturn,
    handleCreateCashOrder,
    handleFillFromStock,
    insertCreated,
  });
  const committed = (pick: (h: typeof latest.current) => unknown) => () => {
    if (commitActiveField().invalid) {
      message.warning(INVALID_INPUTS_HINT);
      return;
    }
    void pick(latest.current);
  };

  const cashTarget = cashOrderTargetType(type);
  const notSaved = documentId ? false : 'Документ ещё не сохранён';
  const actions = useHotkeyActions({
    ...(isDeleted
      ? { 'doc.primary': { label: 'Восстановить', hint: false, run: committed((h) => h.handleRestore()) } }
      : status === 'DRAFT'
        ? {
            'doc.primary': { alwaysRun: true, disabled: postBlockReason ?? false, run: committed((h) => h.handlePost(true)) },
            'doc.post': { alwaysRun: true, disabled: postBlockReason ?? false, run: committed((h) => h.handlePost(false)) },
            'doc.save': { label: 'Сохранить черновик', alwaysRun: true, disabled: saveBlockReason ?? false, run: committed((h) => h.handleSaveDraft(false)) },
            'doc.delete': { hint: false, disabled: notSaved, run: committed((h) => h.handleDelete()) },
          }
        : status === 'POSTED'
          ? {
              'doc.primary': { label: 'Сохранить и закрыть', alwaysRun: true, disabled: postBlockReason ?? false, run: committed((h) => h.handleSaveRepost(true)) },
              'doc.save': { label: 'Сохранить', alwaysRun: true, disabled: postBlockReason ?? false, run: committed((h) => h.handleSaveRepost(false)) },
              'doc.print': { hint: false, disabled: notSaved, run: committed((h) => h.handlePrint()) },
              'doc.unpost': { hint: false, run: committed((h) => h.handleUnpost()) },
              'doc.cancel': { hint: false, run: committed((h) => h.handleCancel()) },
              'doc.delete': { hint: false, run: committed((h) => h.handleDelete()) },
              ...((type === 'SALE' || type === 'PURCHASE') && documentId
                ? { 'doc.createReturn': { hint: false, run: committed((h) => h.handleCreateReturn()) } }
                : {}),
            }
          : { 'doc.primary': { label: 'Вернуть в черновик', hint: false, run: committed((h) => h.handleReturnToDraft()) } }),
    'doc.duplicate': { hint: false, disabled: notSaved, run: committed((h) => h.handleDuplicate()) },
    ...(cashTarget && !isReadOnly
      ? {
          'doc.createCashOrder': {
            label: cashTarget === 'CASH_IN' ? 'Создать приходный ордер' : 'Создать расходный ордер',
            hint: false,
            disabled: header.counterpartyId ? false : 'Укажите контрагента',
            run: committed((h) => h.handleCreateCashOrder(cashTarget)),
          },
        }
      : {}),
    ...(type === 'INVENTORY' && !isReadOnly ? { 'doc.fillFromStock': { hint: false, run: committed((h) => h.handleFillFromStock()) } } : {}),
    'doc.toGrid': { hint: false, run: () => void gridApi.focusCell(gridApi.focusedRow() ?? 0, 0) },
    'doc.toHeader': { hint: false, run: () => void (type === 'WRITE_OFF' || type === 'INVENTORY' ? focusComment() : focusCounterparty()) },
    ...(isReadOnly
      ? {}
      : {
          'doc.addCategory': { hint: false, run: () => openNewCategory(lines[gridApi.focusedRow() ?? -1]?.key) },
          'grid.addRow': { run: () => gridApi.addRow() },
          'grid.removeRow': { run: () => gridApi.removeFocusedRow() },
        }),
  });

  const baseColumns = buildColumns(
    type,
    updateLine,
    selectCategory,
    isReadOnly,
    openNewCategory,
    (category) => windows.open({ kind: 'category', id: category.id }),
    actions.get('doc.addCategory'),
    isMobile,
  );
  const columns: LinesGridColumn<EditorLine>[] = isMobile ? withMobileMeta(baseColumns) : baseColumns;

  /** Телефон: пояснение к строке (почему она подсвечена) — на карточке и вверху листа строки. Тексты
   *  предупреждений начинаются с «Строка N —» или с названия товара в «…» (см. `windowWarnings`). */
  function lineNotice(line: EditorLine): string | null {
    if (!line.categoryId || isReadOnly) return null;
    if (line.archived) return 'Товар в архиве — замените его или верните из архива';
    if (!warningsView.lineNos.has(line.lineNo)) return null;
    const own = warningsView.texts.filter((t) => t.startsWith(`Строка ${line.lineNo} —`) || (!!line.categoryName && t.startsWith(`«${line.categoryName}»`)));
    return own.length > 0 ? own.join('; ') : 'Есть предупреждение — см. под списком строк';
  }

  return (
    <>
      <PreviewConsequencesDialog diff={pendingDiff} warningTexts={pendingDiff ? warningTexts(pendingDiff.warnings) : []} onResolve={resolvePending} />
      {isMobile ? renderActionsMenu() : null}
      <UnsavedChangesDialog
        open={showUnsavedDialog}
        saveLabel={status === 'DRAFT' ? 'Сохранить черновик' : 'Сохранить'}
        saving={busy}
        onSave={() => void handleSaveAndClose()}
        onDiscard={() => {
          setShowUnsavedDialog(false);
          onClose();
        }}
        onCancel={() => setShowUnsavedDialog(false)}
      />
      <FloatingWindow
        kind={`document:${type}`}
        title={windowTitle(type, doc, isNew, isNew ? copyOfLabel : undefined)}
        onClose={handleClose}
        footer={isMobile ? renderMobileFooter() : renderFooter()}
        hotkeys={actions.bindings}
        initialFocus={isNew ? 'auto' : 'window'}
      >
      <InvalidInputsContext.Provider value={invalidInputs.api}>
      {isMobile ? renderMobileBody() : (
      <Flex vertical gap={8}>
        <Flex gap={8} wrap>
          {/* §12.6: у копии документа фокус встаёт на «Дата»; у нового документа — на «Контрагент». */}
          <Flex vertical style={{ minWidth: 140 }} data-autofocus={copyOf ? true : undefined}>
            <Typography.Text style={{ fontSize: 12 }}>Дата</Typography.Text>
            <DatePicker
              value={dayjs(header.docDate)}
              format="DD.MM.YYYY"
              disabled={isReadOnly}
              onChange={(d) => {
                setDirty(true);
                setHeader((h) => ({ ...h, docDate: d ? d.format('YYYY-MM-DD') : h.docDate }));
              }}
            />
          </Flex>
          <Flex vertical style={{ minWidth: 220, flex: 1 }} ref={attachCounterparty} data-autofocus={!copyOf && type !== 'WRITE_OFF' && type !== 'INVENTORY' ? true : undefined}>
            <Typography.Text style={{ fontSize: 12 }}>Контрагент</Typography.Text>
            <CounterpartySelect
              value={header.counterpartyId}
              current={counterpartyRef}
              disabled={isReadOnly}
              onChange={(v) => {
                setDirty(true);
                setHeader((h) => ({ ...h, counterpartyId: v }));
              }}
            />
          </Flex>
          <Flex vertical style={{ minWidth: 200, flex: 2 }} ref={attachComment}>
            <Typography.Text style={{ fontSize: 12 }}>Комментарий</Typography.Text>
            <Input
              value={header.comment}
              disabled={isReadOnly}
              onChange={(e) => {
                setDirty(true);
                setHeader((h) => ({ ...h, comment: e.target.value }));
              }}
            />
          </Flex>
        </Flex>

        {doc?.recalcPending ? <Tag color="warning">Пересчитывается — данные могут быть неактуальны</Tag> : null}

        {isDeleted ? (
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            Документ удалён и в учёте не участвует. «Восстановить» вернёт его {status === 'CANCELLED' ? 'отменённым' : 'черновиком'}.
          </Typography.Text>
        ) : null}
        {hasArchivedLine && !isReadOnly ? (
          <Typography.Text type="danger" style={{ fontSize: 12 }}>
            В выделенных строках товар в архиве — замените его или верните из архива, иначе документ не провести.
          </Typography.Text>
        ) : null}

        {type === 'INVENTORY' && !isReadOnly ? (
          <Flex>
            <HotkeyButton size="small" action={actions.get('doc.fillFromStock')}>
              Заполнить по остаткам
            </HotkeyButton>
          </Flex>
        ) : null}

        <div data-autofocus={isNew && !copyOf && (type === 'WRITE_OFF' || type === 'INVENTORY') ? true : undefined}>
        {renderLinesGrid()}
        </div>

        {warningsView.texts.length > 0 ? (
          <Flex vertical>
            {warningsView.texts.map((text) => (
              <Typography.Text key={text} type="warning" style={{ fontSize: 12 }}>
                {text}
              </Typography.Text>
            ))}
          </Flex>
        ) : null}

        <Flex gap={16}>
          <Typography.Text style={{ fontSize: 12 }}>Позиций: {footer.positions}</Typography.Text>
          {footer.pcs !== null ? <Typography.Text style={{ fontSize: 12 }}>Штук: {formatQty(footer.pcs)}</Typography.Text> : null}
          <Typography.Text style={{ fontSize: 12 }} strong>
            {footer.amountLabel}: {footer.amount ?? 'появится после проведения'}
          </Typography.Text>
        </Flex>

        {renderPaymentBlock()}

        {invalidInputs.hasInvalid && !isReadOnly ? (
          <Typography.Text type="danger" style={{ fontSize: 12 }}>
            {INVALID_INPUTS_HINT}
          </Typography.Text>
        ) : null}

        <AttachmentsBlock documentId={documentId} readOnly={isReadOnly} />
      </Flex>
      )}
      </InvalidInputsContext.Provider>
      </FloatingWindow>
    </>
  );

  function renderLinesGrid() {
    return (
    <LinesGrid
      lines={lines}
      columns={columns}
      getRowKey={(l) => l.key}
      onAddRow={isReadOnly ? () => undefined : addRow}
      onRemoveRow={isReadOnly ? () => undefined : removeRow}
      removeButtonPosition="start"
      readOnly={isReadOnly}
      rowClassName={(l) => {
        if (!l.categoryId) return undefined;
        if (l.archived) return 'cell-error';
        return warningsView.lineNos.has(l.lineNo) ? 'cell-warn' : undefined;
      }}
      gridApiRef={gridApiRef}
      removeRowAction={actions.get('grid.removeRow')}
      isEmptyLine={(l) => !l.categoryId}
      lineNotice={lineNotice}
    />
    );
  }

  /** §7: кнопка «Создать приходный/расходный ордер» — по образцу «Создать возврат» (returnTemplateFrom):
   * открывает настоящее окно кассового ордера, предзаполненное этим документом как основанием
   * (сумма/контрагент/вид операции подтягивает само окно через cash-order-template). Доступна уже на
   * несохранённом черновике — как и «Создать возврат», сначала тихо сохраняет документ. */
  async function handleCreateCashOrder(targetType: 'CASH_IN' | 'CASH_OUT') {
    if (!header.counterpartyId || refuseInvalidInput()) return;
    const currentId = await ensureCreated();
    windows.open({ kind: 'document', type: targetType, baseDocumentId: currentId });
  }

  function renderPaymentBlock() {
    const targetType = cashOrderTargetType(type);
    if (!targetType || isDeleted) return null;
    const isReturn = type === 'RETURN_IN' || type === 'RETURN_OUT';
    const summaryLabel = isReturn ? 'Закрыто' : 'Оплачено';
    const statusLabel = (isReturn ? RETURN_SETTLEMENT_LABEL : PAYMENT_STATUS_LABEL)[doc?.paymentStatus ?? 'UNPAID'];
    const buttonLabel = targetType === 'CASH_IN' ? 'Создать приходный ордер' : 'Создать расходный ордер';
    return (
      <Flex vertical gap={4}>
        <Flex gap={12} align="center" wrap>
          {doc ? (
            <Typography.Text style={{ fontSize: 12 }}>
              {summaryLabel}: {doc.paidAmount} из {doc.totalAmount} ({statusLabel})
            </Typography.Text>
          ) : null}
          {!isReadOnly ? (
            isMobile ? (
              // Телефон: кнопка по ширине, без подсказки при наведении; причину недоступности — текстом.
              <Flex vertical gap={2} style={{ width: '100%' }}>
                <Button size="large" block style={{ minHeight: 44 }} disabled={actions.get('doc.createCashOrder').disabled} onClick={actions.get('doc.createCashOrder').run}>
                  {buttonLabel}
                </Button>
                {actions.get('doc.createCashOrder').disabledReason ? (
                  <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                    {actions.get('doc.createCashOrder').disabledReason}
                  </Typography.Text>
                ) : null}
              </Flex>
            ) : (
              <HotkeyButton size="small" action={actions.get('doc.createCashOrder')}>
                {buttonLabel}
              </HotkeyButton>
            )
          ) : null}
        </Flex>
        {cashOrders && cashOrders.length > 0 ? (
          <Flex vertical gap={2}>
            {cashOrders.map((o) => (
              <Typography.Link
                key={o.cashOrderId}
                style={isMobile ? { fontSize: 14, display: 'block', padding: '8px 0' } : { fontSize: 12 }}
                onClick={() => windows.open({ kind: 'document', type: o.type, id: o.cashOrderId })}
              >
                {formatDate(o.docDate)} · {CASH_ORDER_PREFIX[o.type]}-{o.number}/{o.numberYear} ({CASH_ORDER_STATUS_LABEL[o.status] ?? o.status}) · {o.amount}
              </Typography.Link>
            ))}
          </Flex>
        ) : null}
      </Flex>
    );
  }

  /** §12.6: «Дублировать» — открывает новое окно того же типа, предзаполненное из этого документа. */
  function handleDuplicate() {
    if (!documentId || !doc) return;
    windows.open({ kind: 'document', type, copyOf: documentId, copyOfLabel: `${doc.number}/${doc.numberYear}` });
  }

  function handlePrint() {
    if (!documentId) return;
    window.open(ownerPath(ctx.storeId, `/print/documents/${documentId}`), '_blank');
  }

  /** §11.2/§11.3: возврат из проведённой продажи/прихода. */
  function handleCreateReturn() {
    if (!documentId) return;
    windows.open({ kind: 'document', type: type === 'SALE' ? 'RETURN_IN' : 'RETURN_OUT', returnTemplateFrom: documentId });
  }

  /** Телефон: шапка вертикальной формой (поля на всю ширину), строки — карточками, второстепенное
   *  (комментарий, оплата и ордера, вложения) — в сворачиваемых секциях. Итоги и главные кнопки —
   *  в нижней панели (`renderMobileFooter`). Всё, что есть в десктопном окне, доступно и здесь. */
  function renderMobileBody() {
    const label = (text: string) => (
      <Typography.Text type="secondary" style={{ fontSize: 13 }}>
        {text}
      </Typography.Text>
    );
    const paymentBlock = renderPaymentBlock();
    const commentPreview = header.comment.trim();
    const statusChip = isDeleted ? (
      <Tag>Удалён</Tag>
    ) : !documentId ? (
      <Tag>Новый, не сохранён</Tag>
    ) : status === 'POSTED' ? (
      <Tag color="success">Проведён</Tag>
    ) : status === 'CANCELLED' ? (
      <Tag color="error">Отменён</Tag>
    ) : (
      <Tag>Черновик</Tag>
    );

    return (
      <Flex vertical gap={12}>
        <Flex gap={8} align="center" wrap>
          {statusChip}
          {doc?.recalcPending ? <Tag color="warning">Пересчитывается — данные могут быть неактуальны</Tag> : null}
        </Flex>

        <Flex vertical gap={4} data-autofocus={copyOf ? true : undefined}>
          {label('Дата')}
          <DatePicker
            value={dayjs(header.docDate)}
            format="DD.MM.YYYY"
            disabled={isReadOnly}
            inputReadOnly
            style={{ width: '100%' }}
            onChange={(d) => {
              setDirty(true);
              setHeader((h) => ({ ...h, docDate: d ? d.format('YYYY-MM-DD') : h.docDate }));
            }}
          />
        </Flex>
        <Flex vertical gap={4} ref={attachCounterparty} data-autofocus={!copyOf && type !== 'WRITE_OFF' && type !== 'INVENTORY' ? true : undefined}>
          {label('Контрагент')}
          <CounterpartySelect
            value={header.counterpartyId}
            current={counterpartyRef}
            disabled={isReadOnly}
            onChange={(v) => {
              setDirty(true);
              setHeader((h) => ({ ...h, counterpartyId: v }));
            }}
          />
        </Flex>

        <Collapse
          size="large"
          defaultActiveKey={type === 'WRITE_OFF' || type === 'INVENTORY' ? ['comment'] : []}
          items={[
            {
              key: 'comment',
              label: (
                <Flex gap={8} justify="space-between" align="baseline">
                  <span>Комментарий</span>
                  {commentPreview ? (
                    <Typography.Text type="secondary" ellipsis style={{ fontSize: 13, maxWidth: '60%' }}>
                      {commentPreview}
                    </Typography.Text>
                  ) : null}
                </Flex>
              ),
              forceRender: true,
              children: (
                <div ref={attachComment}>
                  <Input
                    value={header.comment}
                    disabled={isReadOnly}
                    enterKeyHint="done"
                    onChange={(e) => {
                      setDirty(true);
                      setHeader((h) => ({ ...h, comment: e.target.value }));
                    }}
                  />
                </div>
              ),
            },
          ]}
        />

        {isDeleted ? (
          <Typography.Text type="secondary">
            Документ удалён и в учёте не участвует. «Восстановить» вернёт его {status === 'CANCELLED' ? 'отменённым' : 'черновиком'}.
          </Typography.Text>
        ) : null}
        {hasArchivedLine && !isReadOnly ? (
          <Typography.Text type="danger">В выделенных строках товар в архиве — замените его или верните из архива, иначе документ не провести.</Typography.Text>
        ) : null}

        {type === 'INVENTORY' && !isReadOnly ? (
          <Button size="large" block style={{ minHeight: 44 }} disabled={actions.get('doc.fillFromStock').disabled} onClick={actions.get('doc.fillFromStock').run}>
            Заполнить по остаткам
          </Button>
        ) : null}

        <div data-autofocus={isNew && !copyOf && (type === 'WRITE_OFF' || type === 'INVENTORY') ? true : undefined}>{renderLinesGrid()}</div>

        {warningsView.texts.length > 0 ? (
          <Flex vertical gap={2}>
            {warningsView.texts.map((text) => (
              <Typography.Text key={text} type="warning" style={{ overflowWrap: 'anywhere' }}>
                {text}
              </Typography.Text>
            ))}
          </Flex>
        ) : null}

        {invalidInputs.hasInvalid && !isReadOnly ? <Typography.Text type="danger">{INVALID_INPUTS_HINT}</Typography.Text> : null}

        <Collapse
          size="large"
          items={[
            ...(paymentBlock ? [{ key: 'payment', label: 'Оплата и ордера', children: paymentBlock }] : []),
            { key: 'attachments', label: 'Вложения', forceRender: true, children: <AttachmentsBlock documentId={documentId} readOnly={isReadOnly} hideTitle /> },
          ]}
        />
      </Flex>
    );
  }

  /** Телефон: липкая нижняя панель — итоги (всегда на виду), причина недоступности главного
   *  действия словами (подсказки при наведении на телефоне нет), главная кнопка на всю ширину и «⋯». */
  function renderMobileFooter() {
    if (isFetching && documentId) return <Typography.Text type="secondary">Загрузка…</Typography.Text>;

    const primary = actions.get('doc.primary');
    const primaryLabel = isDeleted ? 'Восстановить' : status === 'DRAFT' ? 'Провести и закрыть' : status === 'POSTED' ? 'Сохранить и закрыть' : 'Вернуть в черновик';
    const isReturn = type === 'RETURN_IN' || type === 'RETURN_OUT';
    const paymentSummary =
      doc && cashTarget && !isDeleted
        ? `${isReturn ? 'Закрыто' : 'Оплачено'}: ${doc.paidAmount} из ${doc.totalAmount} (${(isReturn ? RETURN_SETTLEMENT_LABEL : PAYMENT_STATUS_LABEL)[doc.paymentStatus ?? 'UNPAID']})`
        : null;

    return (
      <Flex vertical gap={8}>
        <Flex vertical gap={2} className="tabular-nums">
          <Flex wrap gap="0 12px" style={{ fontSize: 13 }}>
            <span>Позиций: {footer.positions}</span>
            {footer.pcs !== null ? <span>Штук: {formatQty(footer.pcs)}</span> : null}
          </Flex>
          <Typography.Text strong style={{ overflowWrap: 'anywhere' }}>
            {footer.amountLabel}: {footer.amount ?? 'появится после проведения'}
          </Typography.Text>
          {paymentSummary ? <Typography.Text style={{ fontSize: 13 }}>{paymentSummary}</Typography.Text> : null}
        </Flex>
        {primary.disabledReason ? (
          <Typography.Text type="warning" style={{ fontSize: 13 }}>
            {primary.disabledReason}
          </Typography.Text>
        ) : null}
        <Flex gap={8}>
          <Button type="primary" size="large" block loading={busy} disabled={primary.disabled} onClick={primary.run} style={{ minHeight: 48, flex: 1 }}>
            {primaryLabel}
          </Button>
          <Button size="large" aria-label="Другие действия" icon={<EllipsisOutlined />} onClick={() => setMenuOpen(true)} style={{ flex: '0 0 48px', width: 48, minHeight: 48, padding: 0 }} />
        </Flex>
      </Flex>
    );
  }

  /** Телефон: меню «⋯» — все остальные действия документа крупными пунктами (те же условия показа и
   *  блокировки, что у кнопок десктопного подвала); причина блокировки — текстом под пунктом. */
  function renderActionsMenu() {
    interface MenuItem {
      key: string;
      label: string;
      run: () => void;
      disabled: boolean;
      reason?: string;
      danger?: boolean;
    }
    const item = (id: HotkeyActionId, label: string, danger?: boolean): MenuItem => {
      const action = actions.get(id);
      return { key: id, label, run: action.run, disabled: action.disabled || busy, reason: action.disabledReason, danger };
    };
    const items: MenuItem[] = [];
    if (isDeleted) {
      // «Восстановить» — главная кнопка панели, в меню только общие действия.
    } else if (status === 'DRAFT') {
      items.push(item('doc.post', 'Провести'), item('doc.save', 'Сохранить черновик'), item('doc.delete', 'Удалить', true));
    } else if (status === 'POSTED') {
      items.push(item('doc.save', 'Сохранить'));
      if ((type === 'SALE' || type === 'PURCHASE') && documentId) items.push(item('doc.createReturn', 'Создать возврат'));
      if (documentId) items.push(item('doc.print', 'Печать'));
      items.push(item('doc.unpost', 'Снять проведение'), item('doc.cancel', 'Отменить документ'), item('doc.delete', 'Удалить', true));
    }
    if (cashTarget && !isReadOnly) items.push(item('doc.createCashOrder', cashTarget === 'CASH_IN' ? 'Создать приходный ордер' : 'Создать расходный ордер'));
    items.push(item('doc.duplicate', 'Дублировать'));
    items.push({ key: 'close', label: 'Закрыть документ', run: handleClose, disabled: false });

    return (
      <BottomSheet open={menuOpen} onClose={() => setMenuOpen(false)} title="Действия" height="auto">
        <Flex vertical gap={8}>
          {items.map((it) => (
            <div key={it.key}>
              <Button
                block
                size="large"
                danger={it.danger}
                disabled={it.disabled}
                style={{ minHeight: 48, justifyContent: 'flex-start' }}
                onClick={() => {
                  setMenuOpen(false);
                  it.run();
                }}
              >
                {it.label}
              </Button>
              {it.reason ? (
                <Typography.Text type="secondary" style={{ fontSize: 13, display: 'block', marginTop: 2 }}>
                  {it.reason}
                </Typography.Text>
              ) : null}
            </div>
          ))}
        </Flex>
      </BottomSheet>
    );
  }

  function renderFooter() {
    if (isFetching && documentId) return <Typography.Text type="secondary">Загрузка…</Typography.Text>;

    const closeButton = (
      <HotkeyTip catalogId="window.close">
        <Button onClick={handleClose}>Закрыть</Button>
      </HotkeyTip>
    );

    if (isDeleted) {
      return (
        <Space wrap>
          <HotkeyButton type="primary" loading={busy} action={actions.get('doc.primary')}>
            Восстановить
          </HotkeyButton>
          <HotkeyButton action={actions.get('doc.duplicate')}>Дублировать</HotkeyButton>
          {closeButton}
        </Space>
      );
    }
    if (status === 'DRAFT') {
      return (
        <Space wrap>
          <HotkeyButton type="primary" loading={busy} action={actions.get('doc.primary')}>
            Провести и закрыть
          </HotkeyButton>
          <HotkeyButton loading={busy} action={actions.get('doc.post')}>
            Провести
          </HotkeyButton>
          <HotkeyButton loading={busy} action={actions.get('doc.save')}>
            Сохранить черновик
          </HotkeyButton>
          <HotkeyButton danger loading={busy} action={actions.get('doc.delete')}>
            Удалить
          </HotkeyButton>
          <HotkeyButton action={actions.get('doc.duplicate')}>Дублировать</HotkeyButton>
          {closeButton}
        </Space>
      );
    }
    if (status === 'POSTED') {
      return (
        <Space wrap>
          <HotkeyButton type="primary" loading={busy} action={actions.get('doc.primary')}>
            Сохранить и закрыть
          </HotkeyButton>
          <HotkeyButton loading={busy} action={actions.get('doc.save')}>
            Сохранить
          </HotkeyButton>
          {(type === 'SALE' || type === 'PURCHASE') && documentId ? <HotkeyButton action={actions.get('doc.createReturn')}>Создать возврат</HotkeyButton> : null}
          {documentId ? <HotkeyButton action={actions.get('doc.print')}>Печать</HotkeyButton> : null}
          <HotkeyButton loading={busy} action={actions.get('doc.unpost')}>
            Снять проведение
          </HotkeyButton>
          <HotkeyButton loading={busy} action={actions.get('doc.cancel')}>
            Отменить
          </HotkeyButton>
          <HotkeyButton danger loading={busy} action={actions.get('doc.delete')}>
            Удалить
          </HotkeyButton>
          <HotkeyButton action={actions.get('doc.duplicate')}>Дублировать</HotkeyButton>
          {closeButton}
        </Space>
      );
    }
    // CANCELLED
    return (
      <Space wrap>
        <HotkeyButton loading={busy} action={actions.get('doc.primary')}>
          Вернуть в черновик
        </HotkeyButton>
        <HotkeyButton action={actions.get('doc.duplicate')}>Дублировать</HotkeyButton>
        {closeButton}
      </Space>
    );
  }

}

/** Телефон: значения столбцов с полями ввода для карточки строки — сами поля живут в листе
 *  редактирования (`render`), а на карточке нужен текст из того же состояния строки. Столбцы-значения
 *  (остаток, количество, сумма…) карточка рисует их же `render`. */
const MOBILE_CARD_VALUE: Record<string, (line: EditorLine) => ReactNode> = {
  boxes: (l) => l.boxes,
  pcsPerBox: (l) => l.pcsPerBox,
  price: (l) => l.unitPrice,
  wholesale: (l) => l.wholesalePrice,
  markup: (l) => l.markupPct,
  isNew: (l) => (l.isNew ? 'Да' : 'Нет'),
  isPromo: (l) => (l.isPromo ? 'Да' : 'Нет'),
  expiry: (l) => (l.expiryDate ? dayjs(l.expiryDate).format('DD.MM.YYYY') : null),
  countedBoxes: (l) => l.countedBoxes ?? null,
  countedLoose: (l) => l.countedLoose ?? null,
  reason: (l) => WRITE_OFF_REASON_OPTIONS.find((o) => o.value === l.reason)?.label ?? null,
  batch: (l) => (l.batchId ? 'Выбрана' : 'LIFO'),
};

/** Телефон: длинные поля (партия) в листе строки — на всю ширину, остальные идут по два в ряд. */
const MOBILE_WIDE_COLUMNS = new Set(['batch']);

function withMobileMeta(columns: LinesGridColumn<EditorLine>[]): LinesGridColumn<EditorLine>[] {
  return columns.map((col) => {
    const card = MOBILE_CARD_VALUE[col.key];
    const wide = MOBILE_WIDE_COLUMNS.has(col.key);
    if (!card && !wide) return col;
    return { ...col, mobile: { ...col.mobile, ...(card ? { card } : {}), ...(wide ? { wide: true } : {}) } };
  });
}

function buildColumns(
  type: EditorDocumentType,
  updateLine: (key: string, patch: Partial<EditorLine>) => void,
  selectCategory: (line: EditorLine, rowIndex: number, category: CategoryDto) => void,
  readOnly: boolean,
  onAddCategory: (lineKey: string) => void,
  onEditCategory: (category: CategoryDto) => void,
  addCategoryAction: BoundAction,
  isMobile: boolean,
): LinesGridColumn<EditorLine>[] {
  const categoryCol: LinesGridColumn<EditorLine> = {
    key: 'category',
    title: 'Товар',
    width: 240,
    // Телефон: на карточке — название товара (поиск и «+» живут в листе строки).
    mobile: {
      title: true,
      wide: true,
      card: (line) =>
        line.categoryId ? (
          `${line.categoryName ?? '—'}${line.archived ? ' (в архиве)' : ''}`
        ) : (
          <span style={{ color: '#8c8c8c', fontWeight: 400 }}>Товар не выбран</span>
        ),
    },
    render: (line, rowIndex) => (
      <Flex gap={4} align="center">
        {readOnly ? null : isMobile ? (
          <Button
            type="primary"
            shape="circle"
            style={{ backgroundColor: '#52c41a', borderColor: '#52c41a', flex: '0 0 auto' }}
            tabIndex={-1}
            aria-label="Добавить категорию"
            icon={<PlusOutlined />}
            onClick={() => onAddCategory(line.key)}
          />
        ) : (
          <HotkeyTip action={addCategoryAction} display="inline-flex">
            <Button
              type="primary"
              size="small"
              shape="circle"
              style={{ backgroundColor: '#52c41a', borderColor: '#52c41a', flex: '0 0 auto' }}
              tabIndex={-1}
              aria-label="Добавить категорию"
              icon={<PlusOutlined />}
              onClick={() => onAddCategory(line.key)}
            />
          </HotkeyTip>
        )}
        <CategorySearchSelect
          value={line.categoryId}
          current={line.categoryId && line.categoryName ? { id: line.categoryId, name: line.categoryName, isArchived: line.archived } : undefined}
          disabled={readOnly}
          navRow={rowIndex}
          navCol={0}
          onChange={(_id, category) => selectCategory(line, rowIndex, category)}
          onEdit={readOnly ? undefined : onEditCategory}
        />
      </Flex>
    ),
  };

  if (type === 'SALE') {
    return [
      categoryCol,
      {
        key: 'stock',
        title: 'Остаток',
        width: 140,
        render: (line) => <Typography.Text style={{ fontSize: 12 }}>{line.stock ? formatStock(line.stock) : '—'}</Typography.Text>,
      },
      {
        key: 'boxes',
        title: 'Коробок',
        width: 80,
        align: 'right',
        render: (line, rowIndex) => (
          <IntInput value={line.boxes} onChange={(v) => updateLine(line.key, { boxes: v })} navRow={rowIndex} navCol={1} disabled={readOnly} />
        ),
      },
      {
        key: 'pcsPerBox',
        title: 'Штук в коробке',
        width: 100,
        align: 'right',
        render: (line, rowIndex) => (
          <QtyInput value={line.pcsPerBox} onChange={(v) => updateLine(line.key, { pcsPerBox: v })} positive max={MAX_PCS_PER_BOX} navRow={rowIndex} navCol={2} disabled={readOnly} />
        ),
      },
      { key: 'qty', title: 'Количество', width: 90, align: 'right', render: (line) => <span>{formatQty(qtyOf(line))}</span> },
      {
        key: 'price',
        title: 'Цена',
        width: 100,
        align: 'right',
        render: (line, rowIndex) => (
          <MoneyInput value={line.unitPrice} onChange={(v) => updateLine(line.key, { unitPrice: v })} navRow={rowIndex} navCol={3} disabled={readOnly} />
        ),
      },
      { key: 'amount', title: 'Сумма', width: 100, align: 'right', render: (line) => <span>{amountOf(line)}</span> },
      {
        key: 'cost',
        title: 'Себестоимость',
        width: 110,
        align: 'right',
        render: (line) => <span>{line.costAmount ?? '—'}{line.costIsProvisional ? ' (пред.)' : ''}</span>,
      },
    ];
  }

  if (type === 'PURCHASE') {
    return [
      categoryCol,
      {
        key: 'boxes',
        title: 'Коробок',
        width: 80,
        align: 'right',
        render: (line, rowIndex) => (
          <IntInput value={line.boxes} onChange={(v) => updateLine(line.key, { boxes: v })} navRow={rowIndex} navCol={1} disabled={readOnly} />
        ),
      },
      {
        key: 'pcsPerBox',
        title: 'Штук в коробке',
        width: 100,
        align: 'right',
        render: (line, rowIndex) => (
          <QtyInput value={line.pcsPerBox} onChange={(v) => updateLine(line.key, { pcsPerBox: v })} positive max={MAX_PCS_PER_BOX} navRow={rowIndex} navCol={2} disabled={readOnly} />
        ),
      },
      { key: 'qty', title: 'Количество', width: 90, align: 'right', render: (line) => <span>{formatQty(qtyOf(line))}</span> },
      {
        key: 'price',
        title: 'Цена за ед.',
        width: 100,
        align: 'right',
        render: (line, rowIndex) => (
          <MoneyInput value={line.unitPrice} onChange={(v) => updateLine(line.key, { unitPrice: v, lastEdited: line.lastEdited })} navRow={rowIndex} navCol={3} disabled={readOnly} />
        ),
      },
      { key: 'amount', title: 'Сумма закупки', width: 100, align: 'right', render: (line) => <span>{amountOf(line)}</span> },
      {
        key: 'wholesale',
        title: 'Оптовая цена',
        width: 100,
        align: 'right',
        render: (line, rowIndex) => (
          <MoneyInput
            value={line.wholesalePrice}
            onChange={(v) => updateLine(line.key, { wholesalePrice: v, lastEdited: 'wholesale' })}
            navRow={rowIndex}
            navCol={4}
            disabled={readOnly}
          />
        ),
      },
      {
        key: 'markup',
        title: '%',
        width: 70,
        align: 'right',
        render: (line, rowIndex) => (
          <MoneyInput
            value={line.markupPct}
            onChange={(v) => updateLine(line.key, { markupPct: v, lastEdited: 'markup' })}
            navRow={rowIndex}
            navCol={5}
            disabled={readOnly}
          />
        ),
      },
      {
        key: 'isNew',
        title: 'Новинка',
        width: 70,
        align: 'center',
        render: (line) => <Checkbox checked={line.isNew} disabled={readOnly} onChange={(e) => updateLine(line.key, { isNew: e.target.checked })} />,
      },
      {
        key: 'isPromo',
        title: 'Акция',
        width: 60,
        align: 'center',
        render: (line) => <Checkbox checked={line.isPromo} disabled={readOnly} onChange={(e) => updateLine(line.key, { isPromo: e.target.checked })} />,
      },
      {
        key: 'expiry',
        title: 'Срок годности',
        width: 130,
        render: (line) => (
          <DatePicker
            value={line.expiryDate ? dayjs(line.expiryDate) : null}
            format="DD.MM.YYYY"
            disabled={readOnly}
            // Телефон: календарь без экранной клавиатуры, поле на всю ширину ячейки листа строки.
            inputReadOnly={isMobile || undefined}
            style={isMobile ? { width: '100%' } : undefined}
            onChange={(d) => updateLine(line.key, { expiryDate: d ? d.format('YYYY-MM-DD') : null })}
          />
        ),
      },
    ];
  }

  if (type === 'RETURN_IN' || type === 'RETURN_OUT') {
    return [
      categoryCol,
      {
        key: 'base',
        title: type === 'RETURN_IN' ? 'Продано' : 'Куплено',
        width: 90,
        align: 'right',
        render: (line) => <span>{line.soldQty !== undefined ? formatQty(line.soldQty) : '—'}</span>,
      },
      { key: 'returned', title: 'Уже возвращено', width: 110, align: 'right', render: (line) => <span>{formatQty(line.alreadyReturnedQty)}</span> },
      {
        key: 'boxes',
        title: 'Коробок',
        width: 80,
        align: 'right',
        render: (line, rowIndex) => (
          <IntInput value={line.boxes} onChange={(v) => updateLine(line.key, { boxes: v })} navRow={rowIndex} navCol={1} disabled={readOnly} />
        ),
      },
      {
        key: 'pcsPerBox',
        title: 'Штук в коробке',
        width: 100,
        align: 'right',
        render: (line, rowIndex) => (
          <QtyInput value={line.pcsPerBox} onChange={(v) => updateLine(line.key, { pcsPerBox: v })} positive max={MAX_PCS_PER_BOX} navRow={rowIndex} navCol={2} disabled={readOnly} />
        ),
      },
      { key: 'qty', title: 'Количество', width: 90, align: 'right', render: (line) => <span>{formatQty(qtyOf(line))}</span> },
      {
        key: 'price',
        title: 'Цена',
        width: 100,
        align: 'right',
        render: (line, rowIndex) => (
          <MoneyInput value={line.unitPrice} onChange={(v) => updateLine(line.key, { unitPrice: v })} navRow={rowIndex} navCol={3} disabled={readOnly} />
        ),
      },
      { key: 'amount', title: 'Сумма', width: 100, align: 'right', render: (line) => <span>{amountOf(line)}</span> },
      {
        key: 'cost',
        title: 'Себестоимость',
        width: 110,
        align: 'right',
        render: (line) => <span>{line.costAmount ?? '—'}{line.costIsProvisional ? ' (пред.)' : ''}</span>,
      },
      {
        key: 'overBase',
        title: 'Сверх основания',
        width: 110,
        align: 'right',
        render: (line) => <span>{line.qtyOverBase && qty(line.qtyOverBase).gt(0) ? formatQty(line.qtyOverBase) : '—'}</span>,
      },
    ];
  }

  if (type === 'INVENTORY') {
    return [
      categoryCol,
      {
        key: 'accountedSealed',
        title: 'Учёт К',
        width: 80,
        align: 'right',
        render: (line) => <span>{line.stock ? line.stock.sealedBoxes : '—'}</span>,
      },
      {
        key: 'accountedLoose',
        title: 'Учёт Р',
        width: 80,
        align: 'right',
        render: (line) => <span>{line.stock ? formatQty(line.stock.loosePcs) : '—'}</span>,
      },
      {
        key: 'countedBoxes',
        title: 'Факт коробок',
        width: 100,
        align: 'right',
        render: (line, rowIndex) => (
          <IntInput value={line.countedBoxes ?? null} onChange={(v) => updateLine(line.key, { countedBoxes: v })} min={0} navRow={rowIndex} navCol={1} disabled={readOnly} />
        ),
      },
      {
        key: 'countedLoose',
        title: 'Факт россыпь',
        width: 100,
        align: 'right',
        render: (line, rowIndex) => (
          <QtyInput value={line.countedLoose ?? null} onChange={(v) => updateLine(line.key, { countedLoose: v })} max={MAX_LINE_QTY} navRow={rowIndex} navCol={2} disabled={readOnly} />
        ),
      },
      {
        key: 'diff',
        title: 'Разница, шт',
        width: 100,
        align: 'right',
        render: (line) => {
          const diff = inventoryDiff(line);
          if (diff === null) return <span>—</span>;
          const delta = qty(diff);
          return <span style={{ color: delta.isNegative() ? '#cf1322' : delta.gt(0) ? '#389e0d' : undefined }}>{delta.gt(0) ? `+${formatQty(diff)}` : formatQty(diff)}</span>;
        },
      },
    ];
  }

  // WRITE_OFF (§11.4): без контрагента и цены — «Сумма» появится после проведения (себестоимость).
  return [
    categoryCol,
    {
      key: 'stock',
      title: 'Остаток',
      width: 140,
      render: (line) => <Typography.Text style={{ fontSize: 12 }}>{line.stock ? formatStock(line.stock) : '—'}</Typography.Text>,
    },
    {
      key: 'boxes',
      title: 'Коробок',
      width: 80,
      align: 'right',
      render: (line, rowIndex) => (
        <IntInput value={line.boxes} onChange={(v) => updateLine(line.key, { boxes: v })} navRow={rowIndex} navCol={1} disabled={readOnly} />
      ),
    },
    {
      key: 'pcsPerBox',
      title: 'Штук в коробке',
      width: 100,
      align: 'right',
      render: (line, rowIndex) => (
        <QtyInput value={line.pcsPerBox} onChange={(v) => updateLine(line.key, { pcsPerBox: v })} positive max={MAX_PCS_PER_BOX} navRow={rowIndex} navCol={2} disabled={readOnly} />
      ),
    },
    { key: 'qty', title: 'Количество', width: 90, align: 'right', render: (line) => <span>{formatQty(qtyOf(line))}</span> },
    {
      key: 'batch',
      title: 'Партия',
      width: 220,
      render: (line) => <BatchSelect categoryId={line.categoryId} value={line.batchId ?? undefined} disabled={readOnly} onChange={(v) => updateLine(line.key, { batchId: v ?? null })} />,
    },
    {
      key: 'reason',
      title: 'Причина',
      width: 140,
      render: (line) => (
        <Select<WriteOffReason>
          value={line.reason ?? undefined}
          disabled={readOnly}
          options={WRITE_OFF_REASON_OPTIONS}
          style={{ width: '100%' }}
          onChange={(v) => updateLine(line.key, { reason: v })}
        />
      ),
    },
    {
      key: 'cost',
      title: 'Себестоимость',
      width: 110,
      align: 'right',
      render: (line) => <span>{line.costAmount ?? '—'}</span>,
    },
  ];
}
